import { AUTH_MESSAGES } from '@rsmaids/shared/constants/auth';

export interface ErrorHandlingOptions {
  showToast?: (message: string, color?: string, duration?: number) => void;
  onFinally?: () => void;
  context?: 'login' | 'register' | 'reset' | 'passkey' | 'verification' | 'generic';
  customMessages?: {
    fallback?: string;
    [key: string]: string | undefined;
  };
}

export interface AuthErrorResult {
  message: string;
  color: 'danger' | 'warning' | 'success';
  duration: number;
}

// Common auth error types and their handling
export const AUTH_ERROR_PATTERNS = {
  // Firebase/Authentication errors
  'auth/user-not-found': AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS,
  'auth/wrong-password': AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS,
  'auth/invalid-credential': AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS,
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/email-already-in-use': 'Email address is already in use by another account.',
  'auth/weak-password': AUTH_MESSAGES.PASSWORD_MIN_LENGTH,
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled.',
  
  // Network errors
  'NetworkError': 'Network connection error. Please check your internet and try again.',
  'TypeError': 'Connection error. Please try again.',
  
  // Passkey/WebAuthn errors
  'NotAllowedError': 'Authentication was cancelled.',
  'SecurityError': 'Security error during authentication.',
  'InvalidStateError': 'No passkey found for this device. Please set up a passkey first.',
  'NotSupportedError': 'Passkeys are not supported on this device.',
  'AbortError': 'Authentication request was aborted.',
  
  // Context-specific fallbacks
  LOGIN_FALLBACK: AUTH_MESSAGES.LOGIN_FAILED_TRY_AGAIN,
  REGISTER_FALLBACK: AUTH_MESSAGES.REGISTRATION_FAILED,
  RESET_FALLBACK: 'Password reset failed. Please try again.',
  PASSKEY_FALLBACK: 'Passkey operation failed. Please try again.',
  VERIFICATION_FALLBACK: 'Email verification failed. Please try again.',
  GENERIC_FALLBACK: AUTH_MESSAGES.UNEXPECTED_ERROR
} as const;

// Extract error information from various error types
export const parseError = (error: unknown): { message: string; code?: string; name?: string } => {
  if (error instanceof Error) {
    return {
      message: error.message,
      code: (error as Error & { code?: string }).code,
      name: error.name
    };
  }
  
  if (typeof error === 'string') {
    return { message: error };
  }
  
  if (error && typeof error === 'object' && 'message' in error) {
    const errorObj = error as { message: unknown; code?: unknown; name?: unknown };
    return {
      message: String(errorObj.message),
      code: typeof errorObj.code === 'string' ? errorObj.code : undefined,
      name: typeof errorObj.name === 'string' ? errorObj.name : undefined
    };
  }
  
  return { message: 'An unexpected error occurred' };
};

// Get appropriate error message based on context and error type
export const getAuthErrorMessage = (
  error: unknown, 
  context: ErrorHandlingOptions['context'] = 'generic'
): AuthErrorResult => {
  const parsed = parseError(error);
  
  // Check for specific error patterns first
  if (parsed.code && AUTH_ERROR_PATTERNS[parsed.code as keyof typeof AUTH_ERROR_PATTERNS]) {
    return {
      message: AUTH_ERROR_PATTERNS[parsed.code as keyof typeof AUTH_ERROR_PATTERNS],
      color: 'danger',
      duration: 4000
    };
  }
  
  if (parsed.name && AUTH_ERROR_PATTERNS[parsed.name as keyof typeof AUTH_ERROR_PATTERNS]) {
    const message = AUTH_ERROR_PATTERNS[parsed.name as keyof typeof AUTH_ERROR_PATTERNS];
    return {
      message,
      color: parsed.name === 'NotAllowedError' ? 'warning' : 'danger',
      duration: parsed.name === 'InvalidStateError' ? 5000 : 4000
    };
  }
  
  // Check if the error message contains known patterns
  const lowerMessage = parsed.message.toLowerCase();
  if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
    return {
      message: AUTH_ERROR_PATTERNS.NetworkError,
      color: 'warning',
      duration: 4000
    };
  }
  
  if (lowerMessage.includes('credentials') || lowerMessage.includes('password')) {
    return {
      message: AUTH_MESSAGES.LOGIN_FAILED_CREDENTIALS,
      color: 'danger',
      duration: 4000
    };
  }
  
  // Context-specific fallbacks
  const fallbackKey = `${context?.toUpperCase()}_FALLBACK` as keyof typeof AUTH_ERROR_PATTERNS;
  const fallbackMessage = AUTH_ERROR_PATTERNS[fallbackKey] || AUTH_ERROR_PATTERNS.GENERIC_FALLBACK;
  
  return {
    message: parsed.message || fallbackMessage,
    color: 'danger',
    duration: 4000
  };
};

// Main error handler function that can be used in try/catch blocks
export const handleAuthError = async (
  error: unknown,
  options: ErrorHandlingOptions = {}
): Promise<void> => {
  const { showToast, onFinally, context = 'generic', customMessages } = options;
  
  try {
    const errorResult = getAuthErrorMessage(error, context);
    
    // Use custom message if provided
    const finalMessage = customMessages?.fallback || errorResult.message;
    
    if (showToast) {
      showToast(finalMessage, errorResult.color, errorResult.duration);
    }
  } catch (handlingError) {
    // Fallback if error handling itself fails
    if (showToast) {
      showToast(AUTH_MESSAGES.UNEXPECTED_ERROR, 'danger', 4000);
    }
    console.error('Error in error handler:', handlingError);
  } finally {
    if (onFinally) {
      onFinally();
    }
  }
};

// Convenience wrapper for async functions with consistent error handling
export const withErrorHandling = <T extends unknown[], R>(
  asyncFn: (...args: T) => Promise<R>,
  errorOptions: ErrorHandlingOptions
) => {
  return async (...args: T): Promise<R | void> => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      await handleAuthError(error, errorOptions);
    }
  };
};

// Hook-style error handler for React components
export const useAuthErrorHandler = (
  showToast: (message: string, color?: string, duration?: number) => void,
  context: ErrorHandlingOptions['context'] = 'generic'
) => {
  return {
    handleError: (error: unknown, customOptions?: Partial<ErrorHandlingOptions>) =>
      handleAuthError(error, { showToast, context, ...customOptions }),
    
    withErrorHandling: <T extends unknown[], R>(
      asyncFn: (...args: T) => Promise<R>,
      customOptions?: Partial<ErrorHandlingOptions>
    ) => withErrorHandling(asyncFn, { showToast, context, ...customOptions })
  };
};
